import {filter, sortBy} from 'ramda';

import type {EagColumnState} from '../../types/EagColumnState';
import {useEagGridSelector} from '../useEagGridSelector';
import {withColumnStatesCache} from './withColumnStatesCache';

const isVisible = (col: EagColumnState) => col.hidden !== true;
const byOrder = (col: EagColumnState) => col.order;

// Cached selector that returns visible columns ordered for rendering:
//   [pinnedLeft... | unpinned... | pinnedRight...]
// Each group preserves the original order field ascending.
//
// This DOM ordering is required for CSS `position: sticky` with `left`/`right`
// offsets to work correctly on HTML table cells — pinned-left columns must
// appear first in the DOM and pinned-right columns must appear last.
//
// Module-level singleton cache (one withColumnStatesCache instance): the cache
// is keyed on state.columnStates so it is invalidated whenever the array
// reference changes, with a structural-equality short-circuit so mutations to
// unrelated fields (e.g. a non-pinned column's width) do not produce a new
// reference and cause unnecessary re-renders.
const selectOrderedVisibleColumns = withColumnStatesCache((columnStates) => {
  const visible = filter(isVisible, columnStates);

  const pinnedLeft: EagColumnState[] = [];
  const unpinned: EagColumnState[] = [];
  const pinnedRight: EagColumnState[] = [];

  for (const col of visible) {
    if (col.pinned === 'LEFT') {
      pinnedLeft.push(col);
    } else if (col.pinned === 'RIGHT') {
      pinnedRight.push(col);
    } else {
      unpinned.push(col);
    }
  }

  return [
    ...sortBy(byOrder, pinnedLeft),
    ...sortBy(byOrder, unpinned),
    ...sortBy(byOrder, pinnedRight),
  ];
});

export function useOrderedVisibleColumns(): EagColumnState[] {
  return useEagGridSelector(selectOrderedVisibleColumns);
}
